import { formatNumber } from '@/utils'
import { Trans, useLingui } from '@lingui/react/macro'
import clsx from 'clsx'
import { ChevronDown } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import type { Enrollment, EnrollmentSubmissionCounts } from '../api'
import { COLUMN_COUNT, Cell } from './EnrollmentsList'
import { SubmissionsSubTable } from './SubmissionsSubTable'

const WorkCountChips = ({ counts }: { counts: EnrollmentSubmissionCounts }) => {
  const { t } = useLingui()
  return (
    <div className="flex items-center gap-3 whitespace-nowrap">
      {counts.approved > 0 && (
        <span className="text-green inline-flex items-center gap-1" title={t`Approved`}>
          <span aria-hidden>✓</span>
          <span className="font-medium">{counts.approved}</span>
        </span>
      )}
      {counts.inReview > 0 && (
        <span className="text-orange inline-flex items-center gap-1" title={t`In review`}>
          <span aria-hidden>⌛</span>
          <span className="font-medium">{counts.inReview}</span>
        </span>
      )}
      {counts.rejected > 0 && (
        <span className="inline-flex items-center gap-1 text-(--colorError)" title={t`Rejected`}>
          <span aria-hidden>✗</span>
          <span className="font-medium">{counts.rejected}</span>
        </span>
      )}
    </div>
  )
}

const EarningsCell = ({ enrollment, className }: { enrollment: Enrollment; className?: string }) => {
  if (enrollment.project.rewardConfig?.type !== 'usd') return null
  const earnedAmount = formatNumber(enrollment.earnedUsd, 2)
  const pendingAmount = formatNumber(enrollment.pendingUsd, 2)
  return (
    <span className={clsx('flex flex-col', className)}>
      <span className={clsx('font-semibold', enrollment.earnedUsd === 0 && 'text-gray-90/60')}>${earnedAmount}</span>
      {enrollment.pendingUsd > 0 && (
        <span className="text-gray-90 text-body-3">
          <Trans>Pending: +${pendingAmount}</Trans>
        </span>
      )}
    </span>
  )
}

const ChevronButton = ({ isExpanded, onToggle }: { isExpanded: boolean; onToggle: () => void }) => {
  const { t } = useLingui()
  return (
    <button
      type="button"
      aria-label={isExpanded ? t`Collapse enrollment` : t`Expand enrollment`}
      aria-expanded={isExpanded}
      className="text-gray-90 hover:text-orange flex h-6 w-6 items-center justify-center transition-colors"
      onClick={(e) => {
        e.stopPropagation()
        onToggle()
      }}
    >
      <ChevronDown size={18} className={clsx('transition-transform', isExpanded && 'rotate-180')} />
    </button>
  )
}

type EnrollmentRowProps = {
  enrollment: Enrollment
}

export const EnrollmentRow = ({ enrollment }: EnrollmentRowProps) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const toggle = () => setIsExpanded((v) => !v)

  // Desktop / tablet — table row. Hidden below tablet (mobile uses the card below).
  const desktopRow = (
    <tr className="max-tablet:hidden cursor-pointer transition-colors hover:bg-gray-100/20" onClick={toggle}>
      <Cell className="max-w-[300px] overflow-hidden rounded-l-xl border border-r-0 border-solid border-gray-100 align-top text-ellipsis">
        {enrollment.projectName}
      </Cell>
      <Cell className="max-w-[220px] border-y border-solid border-gray-100 align-top">
        <WorkCountChips counts={enrollment.submissionCounts} />
      </Cell>
      <Cell align="right" className="w-30 border-y border-solid border-gray-100 align-top">
        <EarningsCell enrollment={enrollment} />
      </Cell>
      <Cell className="w-10 rounded-r-xl border border-l-0 border-solid border-gray-100 pl-0 align-top" align="right">
        <ChevronButton isExpanded={isExpanded} onToggle={toggle} />
      </Cell>
    </tr>
  )

  // Mobile — stacked card occupying the row.
  const mobileRow: ReactNode = (
    <tr className="tablet:hidden">
      <td colSpan={COLUMN_COUNT} className="border-b border-solid border-gray-100 px-4 py-4">
        <div
          className="-mx-2 -my-2 cursor-pointer rounded-lg p-2 transition-colors hover:bg-gray-100/20"
          onClick={toggle}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="font-medium">{enrollment.projectName}</div>
            <div className="flex items-start gap-2">
              <EarningsCell className="text-right" enrollment={enrollment} />
              <ChevronButton isExpanded={isExpanded} onToggle={toggle} />
            </div>
          </div>
          <WorkCountChips counts={enrollment.submissionCounts} />
        </div>
      </td>
    </tr>
  )

  return (
    <>
      {desktopRow}
      {mobileRow}
      {isExpanded && (
        <tr>
          <td colSpan={COLUMN_COUNT} className="rounded-xl border border-solid border-gray-100 bg-gray-100/10">
            <SubmissionsSubTable enrollmentId={enrollment.id} enabled={isExpanded} />
          </td>
        </tr>
      )}
    </>
  )
}
